import * as THREE from "three";
import type { AABB } from "@palomitas/shared";
import { WORLD_HALF } from "@palomitas/shared";

export interface WorldBuildResult {
  colliders: AABB[];
  /** Ranges where pedestrians may wander freely. */
  walkableBounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  /** Road centerline + extents so cars can drive. */
  road: { z: number; halfWidth: number };
  /** Spawn positions (y = 0) for ground-level food. */
  foodSpawns: THREE.Vector3[];
  /** Spawn positions on short rooftops — always rare, high-value food. */
  rooftopFoodSpawns: THREE.Vector3[];
  /** Center point of the highest south rooftop used by Polly challenge. */
  pollyRoofCenter: THREE.Vector3;
}

export function buildWorld(
  scene: THREE.Scene,
  rng: () => number,
): WorldBuildResult {
  const colliders: AABB[] = [];

  const groundMat = new THREE.MeshLambertMaterial({ color: 0x6b8e57 });
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(WORLD_HALF * 2, WORLD_HALF * 2), groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  const road: WorldBuildResult["road"] = { z: -WORLD_HALF * 0.6, halfWidth: 4 };
  const roadMat = new THREE.MeshLambertMaterial({ color: 0x2a2a2e });
  const roadMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(WORLD_HALF * 2, road.halfWidth * 2),
    roadMat,
  );
  roadMesh.rotation.x = -Math.PI / 2;
  roadMesh.position.set(0, 0.01, road.z);
  roadMesh.receiveShadow = true;
  scene.add(roadMesh);

  const stripeMat = new THREE.MeshBasicMaterial({ color: 0xf5f5dc });
  for (let x = -WORLD_HALF; x < WORLD_HALF; x += 4) {
    const stripe = new THREE.Mesh(new THREE.PlaneGeometry(2, 0.2), stripeMat);
    stripe.rotation.x = -Math.PI / 2;
    stripe.position.set(x + 1, 0.02, road.z);
    scene.add(stripe);
  }

  const pathMat = new THREE.MeshLambertMaterial({ color: 0xa89f88 });
  const path = new THREE.Mesh(new THREE.PlaneGeometry(3, WORLD_HALF * 2), pathMat);
  path.rotation.x = -Math.PI / 2;
  path.position.set(0, 0.02, 0);
  path.receiveShadow = true;
  scene.add(path);

  const rooftopFoodSpawns: THREE.Vector3[] = [];
  const pollyRoofCenter = new THREE.Vector3(0, 35.5, WORLD_HALF - 5.0);
  addBuildingRing(scene, colliders, rng, rooftopFoodSpawns, pollyRoofCenter);
  addBenches(scene, colliders, rng);
  addTrees(scene, colliders, rng);
  addFountain(scene, colliders);
  addLampPosts(scene, colliders);

  const walkableBounds = {
    minX: -WORLD_HALF + 5,
    maxX: WORLD_HALF - 5,
    minZ: -WORLD_HALF + 5,
    maxZ: WORLD_HALF - 5,
  };

  const foodSpawns: THREE.Vector3[] = [];
  for (let i = 0; i < 60; i++) {
    let x = 0, z = 0, tries = 0;
    while (tries++ < 20) {
      x = (rng() * 2 - 1) * (WORLD_HALF - 6);
      z = (rng() * 2 - 1) * (WORLD_HALF - 6);
      if (Math.abs(z - road.z) < road.halfWidth + 1) continue;
      if (!pointIntersectsColliders(x, z, colliders, 0.5)) break;
    }
    foodSpawns.push(new THREE.Vector3(x, 0, z));
  }

  return { colliders, walkableBounds, road, foodSpawns, rooftopFoodSpawns, pollyRoofCenter };
}

function pointIntersectsColliders(x: number, z: number, colliders: AABB[], margin: number): boolean {
  for (const c of colliders) {
    if (
      x > c.min.x - margin && x < c.max.x + margin &&
      z > c.min.z - margin && z < c.max.z + margin
    ) return true;
  }
  return false;
}

function addBuildingRing(
  scene: THREE.Scene,
  colliders: AABB[],
  rng: () => number,
  rooftopFoodSpawns: THREE.Vector3[],
  pollyRoofCenter: THREE.Vector3,
): void {
  const buildingMat = new THREE.MeshLambertMaterial({ color: 0x9ca3af });
  const windowMat = new THREE.MeshBasicMaterial({ color: 0xfde68a });

  const placements = [
    { side: "n", count: 4 }, { side: "s", count: 4 },
    { side: "e", count: 3 }, { side: "w", count: 3 },
  ] as const;

  // Hard-coded, deterministic heights by building slot (left -> right order
  // for each side as i increases in the loop below). No randomness.
  // The two buildings nearest the donut-kid area are the west-side middle
  // and upper slots; both are pinned at 4.2.
  const heightBySide: Record<"n" | "s" | "e" | "w", number[]> = {
    n: [32.1, 15.2, 23.6, 19.1],
    s: [9.4, 10.6, 12.9, 37.0],
    e: [15.4, 6.8, 7.7],
    w: [27.2, 4.2, 4.2],
  };
  let tallestSouthHeight = Number.NEGATIVE_INFINITY;

  for (const { side, count } of placements) {
    for (let i = 0; i < count; i++) {
      const w = 6 + rng() * 4;
      const h = heightBySide[side][i] ?? 9.5;
      const d = 6 + rng() * 4;
      const spread = (WORLD_HALF * 2 - 4) / count;
      const t = -WORLD_HALF + 2 + spread * (i + 0.5);
      let x = 0, z = 0;
      if (side === "n") { x = t; z = -WORLD_HALF + d / 2; }
      if (side === "s") { x = t; z = WORLD_HALF - d / 2; }
      if (side === "e") { x = WORLD_HALF - w / 2; z = t; }
      if (side === "w") { x = -WORLD_HALF + w / 2; z = t; }
      const building = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), buildingMat);
      building.position.set(x, h / 2, z);
      building.castShadow = true;
      building.receiveShadow = true;
      scene.add(building);

      const facadeWidth = side === "n" || side === "s" ? w : d;
      const windows = new THREE.InstancedMesh(
        new THREE.PlaneGeometry(0.4, 0.6),
        windowMat,
        Math.floor((facadeWidth / 1.4) * (h / 1.4)),
      );
      let idx = 0;
      const dummy = new THREE.Object3D();
      const minWindowY = -h / 2 + 1;
      const maxWindowY = h / 2 - 0.8;
      for (let ux = -facadeWidth / 2 + 0.8; ux < facadeWidth / 2 - 0.4; ux += 1.4) {
        for (let wy = minWindowY; wy < maxWindowY; wy += 1.4) {
          if (idx >= windows.count) break;
          if (side === "n") {
            // North buildings face center on their south (+Z) facade.
            dummy.position.set(ux, wy, d / 2 + 0.01);
            dummy.rotation.set(0, 0, 0);
          } else if (side === "s") {
            // South buildings face center on their north (-Z) facade.
            dummy.position.set(ux, wy, -d / 2 - 0.01);
            dummy.rotation.set(0, Math.PI, 0);
          } else if (side === "e") {
            // East buildings face center on their west (-X) facade.
            dummy.position.set(-w / 2 - 0.01, wy, ux);
            dummy.rotation.set(0, -Math.PI / 2, 0);
          } else {
            // West buildings face center on their east (+X) facade.
            dummy.position.set(w / 2 + 0.01, wy, ux);
            dummy.rotation.set(0, Math.PI / 2, 0);
          }
          dummy.updateMatrix();
          windows.setMatrixAt(idx++, dummy.matrix);
        }
      }
      windows.count = idx;
      windows.instanceMatrix.needsUpdate = true;
      building.add(windows);

      colliders.push({
        min: { x: x - w / 2, y: 0, z: z - d / 2 },
        max: { x: x + w / 2, y: h, z: z + d / 2 },
      });

      // Ensure at least one rare-food spawn on every roof.
      const guaranteedFx = x + (rng() - 0.5) * (w - 1.2);
      const guaranteedFz = z + (rng() - 0.5) * (d - 1.2);
      rooftopFoodSpawns.push(new THREE.Vector3(guaranteedFx, h, guaranteedFz));

      // Low roofs still get extra rare-food spawns to reward easier access.
      if (h < 7) {
        const extraCount = Math.floor(rng() * 2); // +0..1 extra
        for (let s = 0; s < extraCount; s++) {
          const fx = x + (rng() - 0.5) * (w - 1.2);
          const fz = z + (rng() - 0.5) * (d - 1.2);
          rooftopFoodSpawns.push(new THREE.Vector3(fx, h, fz));
        }
      }

      // Keep Polly target robust even if exact south roof heights change:
      // always pick the tallest south-side roof center.
      if (side === "s" && h > tallestSouthHeight) {
        tallestSouthHeight = h;
        pollyRoofCenter.set(x, h, z);
      }
    }
  }
}

function addBenches(scene: THREE.Scene, colliders: AABB[], rng: () => number): void {
  const woodMat = new THREE.MeshLambertMaterial({ color: 0x8b5a3c });
  const metalMat = new THREE.MeshLambertMaterial({ color: 0x404040 });

  const positions = [
    [-12, 8], [12, 8], [-12, -8], [12, -8],
    [-20, 20], [20, 20], [0, 18], [0, -18],
    [-8, 30], [8, 30],
  ];

  for (const [px, pz] of positions) {
    const jx = (rng() - 0.5) * 3;
    const jz = (rng() - 0.5) * 3;
    const x = (px ?? 0) + jx;
    const z = (pz ?? 0) + jz;

    const seat = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.08, 0.4), woodMat);
    seat.position.set(x, 0.45, z);
    seat.castShadow = true;
    seat.receiveShadow = true;
    scene.add(seat);

    const back = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.5, 0.08), woodMat);
    back.position.set(x, 0.72, z - 0.18);
    back.castShadow = true;
    scene.add(back);

    for (const side of [-0.7, 0.7]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.45, 0.35), metalMat);
      leg.position.set(x + side, 0.22, z);
      scene.add(leg);
    }

    colliders.push({
      min: { x: x - 0.85, y: 0, z: z - 0.25 },
      max: { x: x + 0.85, y: 0.95, z: z + 0.2 },
    });
  }
}

function addTrees(scene: THREE.Scene, colliders: AABB[], rng: () => number): void {
  const trunkMat = new THREE.MeshLambertMaterial({ color: 0x6b3e21 });
  const leafMat = new THREE.MeshLambertMaterial({ color: 0x3f7a3a });

  for (let i = 0; i < 18; i++) {
    let x = 0, z = 0, tries = 0;
    while (tries++ < 15) {
      x = (rng() * 2 - 1) * (WORLD_HALF - 10);
      z = (rng() * 2 - 1) * (WORLD_HALF - 10);
      if (Math.abs(z - (-WORLD_HALF * 0.6)) < 6) continue;
      if (Math.abs(x) < 2 && Math.abs(z) < 4) continue;
      if (!pointIntersectsColliders(x, z, colliders, 2)) break;
    }
    const trunkH = 2 + rng() * 1.5;
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, trunkH, 8), trunkMat);
    trunk.position.set(x, trunkH / 2, z);
    trunk.castShadow = true;
    scene.add(trunk);

    const leafR = 1.2 + rng() * 0.6;
    const leaves = new THREE.Mesh(new THREE.SphereGeometry(leafR, 10, 8), leafMat);
    leaves.position.set(x, trunkH + leafR * 0.6, z);
    leaves.castShadow = true;
    scene.add(leaves);

    colliders.push({
      min: { x: x - 0.25, y: 0, z: z - 0.25 },
      max: { x: x + 0.25, y: trunkH, z: z + 0.25 },
    });
  }
}

function addFountain(scene: THREE.Scene, colliders: AABB[]): void {
  const stoneMat = new THREE.MeshLambertMaterial({ color: 0xb8b2a7 });
  const waterMat = new THREE.MeshPhongMaterial({
    color: 0x4a9ec2,
    specular: 0xffffff,
    shininess: 80,
    transparent: true,
    opacity: 0.8,
  });

  const base = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.8, 0.6, 24), stoneMat);
  base.position.set(0, 0.3, 0);
  base.castShadow = true;
  base.receiveShadow = true;
  scene.add(base);

  const water = new THREE.Mesh(new THREE.CylinderGeometry(2.3, 2.3, 0.1, 24), waterMat);
  water.position.set(0, 0.62, 0);
  scene.add(water);

  const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.35, 1.5, 12), stoneMat);
  pillar.position.set(0, 1.35, 0);
  pillar.castShadow = true;
  scene.add(pillar);

  colliders.push({
    min: { x: -2.8, y: 0, z: -2.8 },
    max: { x: 2.8, y: 0.6, z: 2.8 },
  });
  colliders.push({
    min: { x: -0.35, y: 0.6, z: -0.35 },
    max: { x: 0.35, y: 2.1, z: 0.35 },
  });
}

function addLampPosts(scene: THREE.Scene, colliders: AABB[]): void {
  const postMat = new THREE.MeshLambertMaterial({ color: 0x222427 });
  const lampMat = new THREE.MeshBasicMaterial({ color: 0xfff4c2 });
  for (const [x, z] of [[-8, -2], [8, -2], [-8, 10], [8, 10], [-24, 0], [24, 0]]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 3, 8), postMat);
    post.position.set(x ?? 0, 1.5, z ?? 0);
    post.castShadow = true;
    scene.add(post);

    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), lampMat);
    lamp.position.set(x ?? 0, 3.1, z ?? 0);
    scene.add(lamp);

    const light = new THREE.PointLight(0xfff4c2, 0.4, 8);
    light.position.set(x ?? 0, 3, z ?? 0);
    scene.add(light);

    colliders.push({
      min: { x: (x ?? 0) - 0.12, y: 0, z: (z ?? 0) - 0.12 },
      max: { x: (x ?? 0) + 0.12, y: 3, z: (z ?? 0) + 0.12 },
    });
  }
}
